import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useWorkspace } from '../WorkspaceContext'
import { useDriveView } from '../DriveViewContext'
import { useCodeRunner } from '../../coderunner/CodeRunnerContext'
import { countFolderContents, findFolderInTree, flattenFolderTree, getFolderPath } from '../../../shared/utils/folderTree'
import { getFileRefIcon, getSnippetExtension, getSnippetIcon } from '../../../shared/utils/getFileIconAndExtensionHelper'
import { isMediaFile } from '../../../shared/utils/isMediaFile'
import { objectIdTimestamp } from '../../../shared/utils/objectIdTimestamp'
import { canDragResource, canDropIntoFolder, moveResourceTo } from '../../../shared/utils/driveDnd'
import { downloadFile, downloadSnippet, downloadMultiple } from '../sidebar/DownloadHelper'
import { normalizeMime } from '../../../shared/utils/normalizeMime'
import { collectFolderSubtreeForSelection } from '../sidebar/Tabs/FilesTabHelpers'
import { canEditResource } from '../../../shared/utils/permissions'
import { deleteResources } from '../../../shared/utils/deleteResource'
import type { Snippet } from '../snippets/SnippetContainer'
import type { FileRef } from '../WorkspaceContext'
import type { Folder, DragPayload, selectRes } from '../sidebar/SideBarTypes'
import FileCard from './FileCard'
import Download from '../../../shared/components/svgIcons/Download'
import TrashIcon from '../../../shared/components/svgIcons/TrashIcon'
import styles from './filelist.module.scss'
import '../sidebar/sidebar.scss'
import SelectIcon from '../../../shared/components/svgIcons/SelectIcon'

type FileListResource =
    | {
        rType: "folder";
        id: string;
        name: string;
        iconSrc: string;
        creatorId: string;
        creatorName?: string;
        contents: string[];
        meta: string;
        raw: Folder;
    }
    | {
        rType: "snippet";
        id: string;
        name: string;
        iconSrc: string;
        creatorId: string;
        creatorName?: string;
        fileSize: number;
        meta: string;
        raw: Snippet;
    }
    | {
        rType: "file";
        id: string;
        name: string;
        iconSrc: string;
        creatorId: string;
        creatorName?: string;
        fileSize: number;
        meta: string;
        raw: FileRef;
    };

type SearchFolderGroup = {
    folderId: string | null;
    folderName: string;
    resources: FileListResource[];
};

const formatBytes = (bytes?: number) => {
    const safeBytes = bytes ?? 0;
    if (safeBytes < 1024) return `${safeBytes} B`;
    if (safeBytes < 1024 * 1024) return `${(safeBytes / 1024).toFixed(1)} KB`;
    return `${(safeBytes / (1024 * 1024)).toFixed(1)} MB`;
};

const snippetSize = (snippet: Snippet) => new Blob([snippet.snipText ?? ""]).size;

const resourceTimestamp = (resource: FileListResource): number => {
    if (resource.rType === "folder") return objectIdTimestamp(resource.id);
    if (resource.rType === "snippet") {
        return resource.raw.updatedAt ? Date.parse(resource.raw.updatedAt) : objectIdTimestamp(resource.id);
    }
    return resource.raw.createdAt ? Date.parse(resource.raw.createdAt) : objectIdTimestamp(resource.id);
};

const matchesTypeFilter = (resource: FileListResource, typeFilter: string): boolean => {
    switch (typeFilter) {
        case 'folders': return resource.rType === 'folder';
        case 'code': return resource.rType === 'snippet' && resource.raw.snippetType === 'code';
        case 'text': return resource.rType === 'snippet' && resource.raw.snippetType === 'text';
        case 'media': return resource.rType === 'file' && isMediaFile(resource.raw);
        case 'documents': return resource.rType === 'file' && !isMediaFile(resource.raw);
        default: return true;
    }
};

const FileList = () => {
    const { roomId } = useParams();
    const {
        Files,
        Snippets,
        Folders,
        membersList,
        memberDetails,
        setOpenSnippets,
        setOpenTabs,
        setActiveFileId,
        setActiveSnippetId,
        setUiMode,
        setSbMode,
        roomDetails,
        roomSettings,
        setDownloadFileErrorPopup,
        setLimitMessage,
        setLimitPopup,
    } = useWorkspace();
    const { setActiveLanguage } = useCodeRunner();

    const { search, setSearch, typeFilter, setTypeFilter, sortField, sortDirection, folderId, setFolderId, setTypeCounts } = useDriveView();
    const clientId = localStorage.getItem('flashroom_id');

    const [draggedItem, setDraggedItem] = useState<DragPayload | null>(null);
    const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);
    const [selectMode, setSelectMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState<selectRes[]>([]);
    const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
    const hadSelectionRef = useRef(false);

    // Once the user has actually selected something, returning to zero
    // selected (by unchecking everything) exits select mode automatically.
    // Opening select mode with nothing selected yet must NOT trigger this.
    useEffect(() => {
        if (!selectMode) {
            hadSelectionRef.current = false;
            return;
        }

        if (selectedIds.length > 0) {
            hadSelectionRef.current = true;
        } else if (hadSelectionRef.current) {
            setSelectMode(false);
            setConfirmBulkDelete(false);
            hadSelectionRef.current = false;
        }
    }, [selectMode, selectedIds]);

    const dragParams = useMemo(() => ({
        clientId,
        memberRole: memberDetails?.role,
        memberCanCreate: memberDetails?.canCreate,
        roomCreatorClientId: roomDetails?.creatorClientId,
        membersList,
    }), [clientId, memberDetails, roomDetails, membersList]);

    const currentFolder = useMemo(
        () => folderId ? findFolderInTree(Folders, folderId) : null,
        [Folders, folderId]
    );

    const folderPath = useMemo(
        () => getFolderPath(Folders, folderId),
        [Folders, folderId]
    );

    // A folder restored from the "stay in this folder on refresh" cookie can
    // point at something that's since been deleted (or that the tree hasn't
    // loaded yet on first render) — fall back to root instead of stranding
    // the user on a permanently empty view.
    useEffect(() => {
        if (folderId && Folders.length > 0 && !currentFolder) {
            setFolderId(null);
        }
    }, [folderId, Folders, currentFolder, setFolderId]);

    const memberNameByClientId = useMemo(() => {
        const map = new Map<string, string>();
        for (const member of membersList) map.set(member.clientId, member.name);
        return map;
    }, [membersList]);

    const mapFolderResource = useMemo(() => (folder: Folder): FileListResource => {
        const contents = [
            ...(folder.folders ?? []).map(child => child._id),
            ...(folder.snippets ?? []),
            ...(folder.fileRefs ?? []),
        ];
        const contentCount = countFolderContents(folder);

        return {
            rType: "folder",
            id: folder._id,
            name: folder.fname,
            iconSrc: `${process.env.PUBLIC_URL}/images/folder-closed-dark.png`,
            creatorId: folder.creatorId,
            creatorName: memberNameByClientId.get(folder.creatorId),
            contents,
            meta: `${contentCount} ${contentCount === 1 ? 'item' : 'items'}`,
            raw: folder,
        };
    }, [memberNameByClientId]);

    const mapSnippetResource = useMemo(() => (snippet: Snippet): FileListResource => {
        const name = `${snippet.snippetName}.${getSnippetExtension(snippet.snippetLanguage || "txt")}`;
        const size = snippetSize(snippet);

        return {
            rType: "snippet",
            id: snippet._id,
            name,
            iconSrc: `${process.env.PUBLIC_URL}/images/${getSnippetIcon(snippet.snippetLanguage || "txt")}.png`,
            creatorId: snippet.createdBy,
            creatorName: memberNameByClientId.get(snippet.createdBy),
            fileSize: size,
            meta: formatBytes(size),
            raw: snippet,
        };
    }, [memberNameByClientId]);

    const mapFileResource = useMemo(() => (file: FileRef): FileListResource => ({
        rType: "file",
        id: file._id,
        name: file.name,
        iconSrc: `${process.env.PUBLIC_URL}/images/${getFileRefIcon(file.name)}`,
        creatorId: file.uploaderClientId ?? "",
        creatorName: file.uploaderClientId ? memberNameByClientId.get(file.uploaderClientId) : undefined,
        fileSize: file.size ?? 0,
        meta: formatBytes(file.size),
        raw: file,
    }), [memberNameByClientId]);

    const visibleResources = useMemo<FileListResource[]>(() => {
        const folderChildren = folderId ? currentFolder?.folders ?? [] : Folders;
        const folderSnippetIds = new Set(currentFolder?.snippets ?? []);
        const folderFileIds = new Set(currentFolder?.fileRefs ?? []);

        const visibleSnippets = folderId
            ? Snippets.filter(snippet => folderSnippetIds.has(snippet._id))
            : Snippets.filter(snippet => snippet.folderParent == null);

        const visibleFiles = folderId
            ? Files.filter(file => folderFileIds.has(file._id))
            : Files.filter(file => file.folderParent == null);

        return [
            ...folderChildren.map(mapFolderResource),
            ...visibleSnippets.map(mapSnippetResource),
            ...visibleFiles.map(mapFileResource),
        ];
    }, [Files, Folders, Snippets, currentFolder, folderId, mapFolderResource, mapSnippetResource, mapFileResource]);

    const searchActive = search.trim().length > 0;
    const groupedResultsActive = searchActive || typeFilter !== 'all';

    const scopedResources = useMemo<FileListResource[]>(() => {
        if (!groupedResultsActive) return [];

        const scopedFolders = folderId
            ? flattenFolderTree(currentFolder?.folders ?? [])
            : flattenFolderTree(Folders);

        const scopedFolderIds = new Set<string>([
            ...(folderId ? [folderId] : []),
            ...scopedFolders.map(folder => folder._id),
        ]);

        const scopedSnippets = folderId
            ? Snippets.filter(snippet => scopedFolderIds.has(snippet.folderParent ?? ''))
            : Snippets;

        const scopedFiles = folderId
            ? Files.filter(file => scopedFolderIds.has(file.folderParent ?? ''))
            : Files;

        return [
            ...scopedFolders.map(mapFolderResource),
            ...scopedSnippets.map(mapSnippetResource),
            ...scopedFiles.map(mapFileResource),
        ];
    }, [groupedResultsActive, folderId, currentFolder, Files, Folders, Snippets, mapFolderResource, mapSnippetResource, mapFileResource]);

    const baseResources = groupedResultsActive ? scopedResources : visibleResources;

    const typeCounts = useMemo(() => {
        const counts = { folders: 0, documents: 0, code: 0, text: 0, media: 0 };
        for (const resource of baseResources) {
            if (resource.rType === 'folder') counts.folders += 1;
            else if (resource.rType === 'snippet') {
                if (resource.raw.snippetType === 'code') counts.code += 1;
                else counts.text += 1;
            } else if (isMediaFile(resource.raw)) counts.media += 1;
            else counts.documents += 1;
        }
        return counts;
    }, [baseResources]);

    useEffect(() => {
        setTypeCounts(typeCounts);
    }, [typeCounts, setTypeCounts]);

    useEffect(() => {
        if (typeFilter !== 'all' && typeCounts[typeFilter] === 0) {
            setTypeFilter('all');
        }
    }, [typeFilter, typeCounts, setTypeFilter]);

    const displayedResources = useMemo(() => {
        const query = search.trim().toLowerCase();

        const filtered = baseResources.filter(resource =>
            matchesTypeFilter(resource, typeFilter) &&
            (!query || resource.name.toLowerCase().includes(query))
        );

        const sorted = [...filtered].sort((a, b) => {
            let cmp: number;
            if (sortField === 'name') {
                cmp = a.name.toLowerCase().localeCompare(b.name.toLowerCase());
            } else if (sortField === 'size') {
                const aSize = a.rType === 'folder' ? 0 : a.fileSize;
                const bSize = b.rType === 'folder' ? 0 : b.fileSize;
                cmp = aSize - bSize;
            } else {
                cmp = resourceTimestamp(a) - resourceTimestamp(b);
            }
            return sortDirection === 'asc' ? cmp : -cmp;
        });

        return sorted;
    }, [baseResources, search, typeFilter, sortField, sortDirection]);

    const getResourceParentId = (resource: FileListResource): string | null => resource.raw.folderParent ?? null;

    const searchResultLayout = useMemo(() => {
        const currentDirectory: FileListResource[] = [];
        const groups = new Map<string, SearchFolderGroup>();

        if (!groupedResultsActive) {
            return { currentDirectory: displayedResources, folderGroups: [] };
        }

        for (const resource of displayedResources) {
            const parentFolderId = getResourceParentId(resource);

            if (parentFolderId === folderId) {
                currentDirectory.push(resource);
                continue;
            }

            const groupKey = parentFolderId ?? '__root__';
            const folderName = parentFolderId
                ? findFolderInTree(Folders, parentFolderId)?.fname ?? 'Folder'
                : roomDetails?.roomName ?? 'My Drive';

            const existingGroup = groups.get(groupKey);
            if (existingGroup) {
                existingGroup.resources.push(resource);
            } else {
                groups.set(groupKey, {
                    folderId: parentFolderId,
                    folderName,
                    resources: [resource],
                });
            }
        }

        return { currentDirectory, folderGroups: Array.from(groups.values()) };
    }, [displayedResources, groupedResultsActive, folderId, Folders, roomDetails]);

    const draggedItemCreatorId = useMemo(() => {
        if (!draggedItem) return null;
        return baseResources.find(r => r.rType === draggedItem.kind && r.id === draggedItem.id)?.creatorId ?? null;
    }, [draggedItem, baseResources]);

    const goBack = () => {
        if (folderPath.length <= 1) {
            setFolderId(null);
            return;
        }
        setFolderId(folderPath[folderPath.length - 2]._id);
    };

    const openResource = (id: string, rType: "file" | "snippet" | "folder") => {
        if (rType === "folder") {
            setFolderId(id);
            return;
        }

        if (rType === "snippet") {
            const snippet = Snippets.find(s => s._id === id);
            if (!snippet) return;

            setOpenSnippets(prev =>
                prev.some(s => s._id === id) ? prev : [snippet, ...prev]
            );
            setOpenTabs(prev =>
                prev.some(tab => tab.resourceType === "snippet" && tab.data._id === id)
                    ? prev
                    : [{ resourceType: "snippet", data: snippet }, ...prev]
            );
            setActiveSnippetId(snippet._id);
            setActiveLanguage(snippet.snippetLanguage || "");
            setActiveFileId({ resourceType: "snippet", id: snippet._id });
            setUiMode("editor");
            setSbMode(prev => prev === "filelist" ? "editor" : prev);
            return;
        }

        const file = Files.find(f => f._id === id);
        if (!file) return;

        setOpenTabs(prev =>
            prev.some(tab => tab.resourceType === "file" && tab.data._id === id)
                ? prev
                : [{ resourceType: "file", data: file }, ...prev]
        );
        setActiveSnippetId(null);
        setActiveLanguage("");
        setActiveFileId({ resourceType: "file", id: file._id });
        setUiMode("editor");
        setSbMode(prev => prev === "filelist" ? "editor" : prev);
    };

    const finishDrop = async (targetFolderId: string | null) => {
        const item = draggedItem;
        setDraggedItem(null);
        setDragOverFolderId(null);
        if (!item || !roomId) return;
        if (item.kind === 'folder' && item.id === targetFolderId) return;
        try {
            await moveResourceTo(roomId, item, targetFolderId);
        } catch {
            // move failures surface via the room's normal error/limit popup flow elsewhere
        }
    };

    const closeSelectModeClick = () => {
        setSelectMode(false);
        setSelectedIds([]);
        setConfirmBulkDelete(false);
    };

    // Selects everything currently eligible/visible in this Drive location
    // (respecting the active search/type filter), expanding folders into
    // their full subtree the same way an individual folder click does.
    const selectAllClick = () => {
        const merged: selectRes[] = [];
        const seen = new Set<string>();
        const add = (item: selectRes) => {
            const key = `${item.resType}:${item.id}`;
            if (!seen.has(key)) {
                seen.add(key);
                merged.push(item);
            }
        };

        for (const resource of displayedResources) {
            if (resource.rType === 'folder') {
                for (const item of collectFolderSubtreeForSelection(resource.raw)) add(item);
            } else {
                add({ id: resource.id, resType: resource.rType });
            }
        }

        setSelectMode(true);
        setSelectedIds(merged);
    };

    const isChecked = (id: string) => selectedIds.some(s => s.id === id);

    const checkClick = (e: React.MouseEvent, id: string, resType: string) => {
        e.stopPropagation();
        setSelectMode(true);
        setSelectedIds(prev => {
            const exists = prev.some(s => s.id === id);
            if (exists) return prev.filter(s => s.id !== id);
            return [...prev, { id, resType }];
        });
    };

    const folderCheckClick = (e: React.MouseEvent, targetFolderId: string) => {
        e.stopPropagation();
        setSelectMode(true);
        setSelectedIds(prev => {
            const isSelected = prev.some(x => x.id === targetFolderId && x.resType === "folder");
            const folderObj = findFolderInTree(Folders, targetFolderId);
            if (!folderObj) return prev;
            const subtree = collectFolderSubtreeForSelection(folderObj);

            if (!isSelected) {
                const existing = new Set(prev.map(x => `${x.resType}:${x.id}`));
                const merged = [...prev];
                for (const item of subtree) {
                    const key = `${item.resType}:${item.id}`;
                    if (!existing.has(key)) merged.push(item);
                }
                return merged;
            }

            const remove = new Set(subtree.map(x => `${x.resType}:${x.id}`));
            return prev.filter(x => !remove.has(`${x.resType}:${x.id}`));
        });
    };

    const downloadSelectedClick = async () => {
        if (!roomId) return;
        try {
            if (selectedIds.length === 1 && selectedIds[0].resType === 'snippet') {
                const selectedSnip = Snippets.find(s => s._id === selectedIds[0].id);
                if (!selectedSnip) return;
                let mime = '';
                let ext = '';
                if (selectedSnip.snippetType === 'text') {
                    mime = "text/plain;charset=utf-8";
                    ext = 'txt';
                } else {
                    const lang = selectedSnip.snippetLanguage;
                    if (!lang) return;
                    mime = normalizeMime(lang);
                    ext = getSnippetExtension(lang) ?? "txt";
                }
                await downloadSnippet(`${selectedSnip.snippetName}.${ext}`, selectedSnip.snipText, mime);
            } else if (selectedIds.length === 1 && selectedIds[0].resType === 'file') {
                const selectedFile = Files.find(f => f._id === selectedIds[0].id);
                if (!selectedFile) return;
                await downloadFile(roomId, selectedFile._id, selectedFile.name);
            } else if (!selectedIds.length) {
                setDownloadFileErrorPopup(true);
                return;
            } else {
                const fileIds = selectedIds.filter(i => i.resType === 'file').map(i => i.id);
                const snippetIds = selectedIds.filter(i => i.resType === 'snippet').map(i => i.id);
                const folderIds = selectedIds.filter(i => i.resType === 'folder').map(i => i.id);

                const selectedFolder =
                    selectedIds.length === 1 && selectedIds[0].resType === 'folder'
                        ? findFolderInTree(Folders, selectedIds[0].id)
                        : null;

                await downloadMultiple(roomId, snippetIds, fileIds, folderIds, {
                    name: selectedFolder?.fname || `${selectedIds.length} selected items`,
                    itemType: selectedFolder ? "folder" : "file",
                });
            }

            closeSelectModeClick();
        } catch {
            setDownloadFileErrorPopup(true);
        }
    };

    // Same creatorId lookup shape canEditResource needs everywhere else —
    // folders included, since a selected folder's own creator/role decides
    // its deletability (its contents are never inspected, see FilesTabHelpers).
    const getCreatorId = (item: selectRes): string | undefined => {
        if (item.resType === 'folder') return findFolderInTree(Folders, item.id)?.creatorId;
        if (item.resType === 'snippet') return Snippets.find(s => s._id === item.id)?.createdBy;
        return Files.find(f => f._id === item.id)?.uploaderClientId;
    };

    const canDeleteItem = (item: selectRes) => {
        const creatorId = getCreatorId(item);
        if (creatorId === undefined) return false;
        return canEditResource({
            creatorId,
            clientId,
            memberRole: memberDetails?.role,
            roomCreatorClientId: roomDetails?.creatorClientId,
            membersList,
        });
    };

    const deletableSelectedIds = selectedIds.filter(canDeleteItem);

    const bulkDeleteClick = () => {
        if (!deletableSelectedIds.length) return;
        setConfirmBulkDelete(true);
    };

    const confirmBulkDeleteClick = async () => {
        if (!roomId) return;
        const toDelete = deletableSelectedIds;
        const blockedCount = selectedIds.length - toDelete.length;

        const { deletedIds, failedIds } = await deleteResources(roomId, toDelete);

        setSelectedIds(prev => prev.filter(s => !deletedIds.includes(s.id)));
        setConfirmBulkDelete(false);

        if (blockedCount > 0 || failedIds.length > 0) {
            if (deletedIds.length > 0) {
                setLimitMessage('Some selected items could not be deleted because you do not have permission.');
            } else {
                setLimitMessage('Could not delete item. Please try again.');
            }
            setLimitPopup(true);
        }
    };

    const renderResourceCard = (resource: FileListResource, showLocation: boolean) => {
        const parentFolderId = getResourceParentId(resource);
        const locationLabel = showLocation && parentFolderId !== folderId
            ? (parentFolderId ? findFolderInTree(Folders, parentFolderId)?.fname : null) ?? (roomDetails?.roomName ?? 'My Drive')
            : undefined;

        return (
            <FileCard
                key={`${resource.rType}-${resource.id}`}
                rType={resource.rType}
                rId={resource.id}
                perm={
                    resource.rType === 'folder' ? resource.raw.folderPermission ?? undefined
                        : resource.rType === 'snippet' ? resource.raw.snipPermissions
                            : undefined
                }
                creatorId={resource.creatorId}
                creatorName={resource.creatorName}
                showCreator={!!roomSettings.trackMembersEnabled}
                contents={resource.rType === "folder" ? resource.contents : undefined}
                fileSize={resource.rType !== "folder" ? resource.fileSize : undefined}
                iconSrc={resource.iconSrc}
                resName={resource.name}
                editName={resource.rType === 'snippet' ? resource.raw.snippetName : resource.rType === 'folder' ? resource.name : undefined}
                meta={resource.meta}
                onOpen={openResource}
                canDrag={canDragResource(resource.creatorId, dragParams)}
                onDragStart={() => setDraggedItem({ kind: resource.rType, id: resource.id })}
                onDragEnd={() => { setDraggedItem(null); setDragOverFolderId(null); }}
                canAcceptDrop={
                    resource.rType === 'folder' &&
                    !!draggedItem &&
                    draggedItemCreatorId !== null &&
                    !(draggedItem.kind === 'folder' && draggedItem.id === resource.id) &&
                    canDropIntoFolder(
                        { creatorId: resource.creatorId, folderPermission: resource.raw.folderPermission },
                        draggedItemCreatorId,
                        dragParams
                    )
                }
                isDragOver={dragOverFolderId === resource.id}
                onDragEnterTarget={() => setDragOverFolderId(resource.id)}
                onDragLeaveTarget={() => setDragOverFolderId(prev => prev === resource.id ? null : prev)}
                onDropTarget={() => void finishDrop(resource.id)}
                locationLabel={locationLabel}
                onLocationClick={() => { setSearch(''); setFolderId(parentFolderId); }}
                selectMode={selectMode}
                isSelected={isChecked(resource.id)}
                onToggleSelect={(e) =>
                    resource.rType === 'folder'
                        ? folderCheckClick(e, resource.id)
                        : checkClick(e, resource.id, resource.rType)
                }
            />
        );
    };

    return (
        <div className={styles.fileList}>

            <div className={styles.fileListHeader}>
                {folderId && (
                    <button
                        type="button"
                        className={styles.backBtn}
                        onClick={goBack}
                        aria-label="Back"
                        title="Back"
                    >
                        <svg viewBox="0 0 24 24" fill="none">
                            <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                )}

                <div className={styles.directoryTrail} aria-label="Current folder">
                    <button
                        type="button"
                        onClick={() => setFolderId(null)}
                        onDragOver={(e) => { if (draggedItem) e.preventDefault(); }}
                        onDrop={(e) => { e.preventDefault(); void finishDrop(null); }}
                    >
                        {roomDetails?.roomName ?? 'My Drive'}
                    </button>

                    {folderPath.map(folder => (
                        <React.Fragment key={folder._id}>
                            <span className={styles.directorySlash}>/</span>
                            <button
                                type="button"
                                onClick={() => setFolderId(folder._id)}
                                onDragOver={(e) => { if (draggedItem) e.preventDefault(); }}
                                onDrop={(e) => { e.preventDefault(); void finishDrop(folder._id); }}
                            >
                                {folder.fname}
                            </button>
                        </React.Fragment>
                    ))}
                </div>

                <label className={styles.fileListSearch} aria-label="Search files">
                    <span aria-hidden="true">
                        <svg viewBox='0 0 24 24'>
                            <circle cx='11' cy='11' r='7' />
                            <path d='M16.5 16.5L21 21' />
                        </svg>
                    </span>
                    <input
                        style={{ cursor: 'text' }}
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search"
                    />
                </label>

                <button
                    type="button"
                    className={`${styles.selectBtn} ${selectMode ? styles.selectBtnActive : ''} quick-flex`}
                    onClick={() => (selectMode ? closeSelectModeClick() : setSelectMode(true))}
                >
                    {selectMode ? (
                        <svg className={styles.selectBtnX} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 111.01 111.01" aria-hidden="true">
                            <path
                                d="M232.21,289.66a10.1,10.1,0,0,1-14.31,0l-38.22-38.22-38.23,38.23a10.13,10.13,0,0,1-14.32-14.32l38.23-38.23L127.14,198.9a10.12,10.12,0,1,1,14.31-14.31l38.22,38.22,38.23-38.23a10.13,10.13,0,0,1,14.32,14.32L194,237.13l38.22,38.22A10.12,10.12,0,0,1,232.21,289.66Z"
                                transform="translate(-124.17 -181.62)"
                            />
                        </svg>
                    ) : (
                        <SelectIcon />
                    )}

                    <span>
                        {selectMode ? 'Cancel' : 'Select'}
                    </span>

                </button>
            </div>
            <div className={styles.fileListSurface}>


                <div className={styles.fileListGrid}>
                    {searchResultLayout.currentDirectory.map(resource => renderResourceCard(resource, false))}

                    {groupedResultsActive && searchResultLayout.folderGroups.map(group => (
                        <section className={styles.searchFolderGroup} key={group.folderId ?? 'root'}>
                            <button
                                type="button"
                                className={styles.searchFolderHeader}
                                onClick={() => { setSearch(''); setFolderId(group.folderId); }}
                            >
                                <img draggable={false} src={`${process.env.PUBLIC_URL}/images/folder-closed-dark.png`} alt="" aria-hidden="true" />
                                <span>
                                    <span className={styles.searchFolderNameText}>{group.folderName}</span>
                                    <svg
                                        className={styles.searchFolderChevron}
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        aria-hidden="true"
                                    >
                                        <path d="M9 18l6-6-6-6" />
                                    </svg>
                                </span>
                                <strong>{group.resources.length} {group.resources.length === 1 ? 'match' : 'matches'}</strong>
                            </button>

                            <div className={styles.searchFolderItems}>
                                {group.resources.map(resource => renderResourceCard(resource, false))}
                            </div>
                        </section>
                    ))}
                </div>

                {!displayedResources.length && (
                    <div className={styles.emptyFileList}>
                        <h2>{baseResources.length ? 'No matches' : 'This folder is empty'}</h2>
                        <p>{baseResources.length ? 'Try a different search or filter.' : 'Add files, snippets, or folders to fill it in.'}</p>
                    </div>
                )}

                {selectMode && (
                    <div className="file-download-options fade-in">
                        {confirmBulkDelete ? (
                            <div className='select-mode-btns'>
                                <span className='selected-lbl'>Delete {deletableSelectedIds.length} item{deletableSelectedIds.length === 1 ? '' : 's'}?</span>

                                <div className='quick-flex nbklsk'>
                                    <div onClick={() => setConfirmBulkDelete(false)} className='delete-pop-btn dp-c-s'>Cancel</div>
                                    <div onClick={() => void confirmBulkDeleteClick()} className='delete-pop-btn dp-y-s'>Yes, Delete</div>
                                </div>
                            </div>
                        ) : (
                            <div className="select-mode-btns">
                                <div className="clickable sm-canc" onClick={closeSelectModeClick}>
                                    Cancel
                                </div>

                                <div className="quick-flex nbklsk">
                                    <button onClick={selectAllClick} className={`${styles.smbSelectAll} quick-flex`}>
                                        <div className={styles.boxOutline} />
                                        Select All
                                    </button>

                                    <button
                                        className={`smb-download-selected ${selectedIds.length ? 'ok-to-download' : 'none-selected'}`}
                                        onClick={() => void downloadSelectedClick()}
                                    >
                                        <Download />
                                        <span>Download</span>
                                    </button>

                                    {deletableSelectedIds.length > 0 && (
                                        <button
                                            className="smb-download-selected smb-delete-selected ok-to-download"
                                            onClick={bulkDeleteClick}
                                        >
                                            <TrashIcon />
                                            <span>Delete</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export default FileList
